<template>
  <div className="flex flex-col min-h-screen overflow-hidden supports-[overflow:clip]:overflow-clip">

    <!-- Page content -->
    <main class="grow flex flex-col items-center justify-center pt-20">
      <section class="m-4 p-4 shadow-md rounded-lg max-w-4xl">
        <div class="text-white markdown-content">
          <h1>PortexAI, Inc. Privacy Policy</h1>

          <p><em>Last Updated: March 13, 2025</em></p>

          <p>At PortexAI, Inc. ("Portex," "we," "us," or "our"), we are committed to protecting your privacy and safeguarding your personal data. This Privacy Policy outlines how we collect, use, disclose, and protect your information when you use our website, products, and services, including our on-chain/off-chain data marketplace and related features (collectively, the "Services").</p>

          <p>By accessing or using our Services, you agree to the collection and use of your information in accordance with this Privacy Policy. If you do not agree with any part of this Policy, please do not use the Services.</p>

          <hr />

          <h3>1. Information We Collect</h3>

          <h4>1.1 Personal Information</h4>

          <p>When you use our Services, we may collect personal information from or about you such as:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, crypto wallet addresses, or other credentials necessary for account creation, authentication, or to facilitate transactions.</li>
            <li><strong>Transaction and Payment Data:</strong> Records of purchases, licensing terms, pricing, and associated on-chain transaction details.</li>
            <li><strong>Communication Data:</strong> Information you voluntarily provide when contacting us for support, inquiries, or feedback.</li>
            <li><strong>Data:</strong> Information about how you access and use the Services, including device information, IP address, browser type, and operating system.</li>
          </ul>

          <h4>1.2 Non-Personal Information</h4>

          <p>We may also collect non-personal information that does not directly identify you. This may include aggregated or anonymized data about usage patterns on our marketplace and related Services.</p>

          <h3>2. Legal Bases for Processing Personal Data</h3>

          <p>We process your personal data based on the following legal grounds:</p>
          <ul>
            <li><strong>Consent (Article 6(1)(a) GDPR):</strong> We rely on your consent when you voluntarily share information, connect a crypto wallet, or opt into certain data-sharing features. You have the right to withdraw your consent at any time.</li>
            <li><strong>Contract Necessity (Article 6(1)(b) GDPR):</strong> Processing is necessary for the performance of a contract, including the facilitation of data purchases or listings.</li>
            <li><strong>Legitimate Interests (Article 6(1)(f) GDPR):</strong> We process data to pursue our legitimate interests in improving and securing our Services, provided that such processing does not outweigh your rights and freedoms.</li>
            <li><strong>Legal Obligations (Article 6(1)(c) GDPR):</strong> We may process your data to comply with legal obligations, such as financial reporting, audit requirements, or responding to legal processes.</li>
          </ul>

          <h3>3. How We Use Your Information</h3>

          <p>We use your information for the following purposes:</p>
          <ul>
            <li><strong>To Provide and Maintain Services:</strong> Facilitate listing, buying, and selling of data products on our marketplace and ensure the functionality of our platform.</li>
            <li><strong>Personalization:</strong> Customize and improve your experience by providing tailored content or recommendations based on your transaction history.</li>
            <li><strong>Communication:</strong> Notify you about updates, account activities, security alerts, and administrative messages.</li>
            <li><strong>Security and Compliance:</strong> Safeguard the integrity of our marketplace, protect against fraud, conduct audits, and comply with applicable regulations.</li>
            <li><strong>Research and Development:</strong> Enhance usability and overall user experience by assessing the performance of our marketplace and analyzing aggregated user behavior.</li>
          </ul>

          <h3>4. Data Ownership and Custodianship</h3>

          <h4>4.1 User Data Ownership</h4>

          <p>We respect your rights to your data. In the context of our marketplace, users maintain ownership of the data they choose to list, access, or provide. Our role is to facilitate transactions and data exchanges, not to co-opt your proprietary information for our own purposes.</p>

          <h4>4.2 Custodianship Agreement</h4>

          <p>By using the Services, you agree that Portex acts as a custodian for certain data elements you entrust to us (e.g., hashed transaction records, licensing preferences, user account details). We will handle such data responsibly and only in accordance with this Policy or as permitted by law.</p>

          <h3>5. Consent and Authorization</h3>

          <h4>5.1 Data Connections</h4>

          <p>If you connect your crypto wallet or choose to link other third-party services, you grant us permission to process relevant information such as address balances, transaction history, or other application data required to facilitate listings, sales, or purchases.</p>

          <h4>5.2 Communications and Support</h4>

          <p>By contacting us through any support channels or providing feedback, you consent to our collection and processing of such data to address your inquiries, troubleshoot issues, or improve the Services.</p>

          <h3>6. Data Sharing and Disclosure</h3>

          <p>We may share your information in the following circumstances:</p>
          <ul>
            <li><strong>Service Providers:</strong> We engage trusted third-party service providers for operations such as hosting, data analytics, payment processing, or security. These providers must adhere to confidentiality obligations and use your personal data only for the services they perform.</li>
            <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, bankruptcy, or sale of assets, your information may be transferred to another entity. We will notify you of any significant change in ownership or use of your personal data.</li>
            <li><strong>Legal Obligations and Protection:</strong> We may disclose your information as required by law or when we believe that such disclosure is necessary to protect our rights, the safety of our users, or to comply with a judicial proceeding, court order, or legal process.</li>
            <li><strong>With Your Consent:</strong> We may share your personal data for other purposes if you have provided explicit consent.</li>
          </ul>

          <h3>7. Data Security</h3>

          <p>We implement robust security measures to protect your personal information from unauthorized access or disclosure. These measures include encryption, secure servers, and internal access controls. However, no method of data transmission or storage is completely immune to risk. While we strive to protect your data, we cannot guarantee its absolute security.</p>

          <h3>8. Data Retention</h3>

          <p>We retain your personal information only as long as necessary for the purposes outlined in this Privacy Policy or to comply with legal obligations, resolve disputes, and enforce our agreements. Retention periods may vary based on the nature of the information and regulatory requirements.</p>

          <h3>9. Your Rights and Choices</h3>

          <h4>9.1 Access and Correction</h4>

          <p>You have the right to request access to or correction of your personal information at any time by logging into your account or contacting us directly.</p>

          <h4>9.2 Deletion</h4>

          <p>You may request the deletion of your personal information, subject to certain legal or contractual obligations. If deletion is permissible, we will endeavor to remove your data promptly.</p>

          <h4>9.3 Withdrawal of Consent</h4>

          <p>If we rely on consent as the legal basis for processing your data, you may withdraw your consent at any time by contacting us or using the available account settings. Withdrawal of consent does not affect the lawfulness of processing prior to withdrawal.</p>

          <h4>9.4 Exercise of Rights</h4>

          <p>To exercise these rights, please contact us at <a href="mailto:privacy@portexai.com">privacy@portexai.com</a>. We may require additional information or documentation from you to verify your identity.</p>

          <h3>10. International Data Transfers</h3>

          <p>Your personal data may be transferred to and processed in countries outside your country of residence, including the United States. We implement appropriate safeguards, such as Standard Contractual Clauses, to protect your data when transferring it across borders.</p>

          <h3>11. Children's Privacy</h3>

          <p>Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that we have inadvertently collected such data, we will take steps to delete it promptly.</p>

          <h3>12. Third-Party Services</h3>

          <p>Our Services may contain links to third-party websites or incorporate third-party tools. This Privacy Policy does not apply to the privacy practices of those third parties, and we are not responsible for their content, security, or privacy policies. We encourage you to review the privacy statements of any third-party sites or services you engage with.</p>

          <h3>13. Changes to This Privacy Policy</h3>

          <p>We may update this Privacy Policy from time to time. Significant changes will be communicated by posting a revised policy on this page and updating the "Last Updated" date. In some instances, we may provide additional or more prominent notice. Your continued use of the Services following any changes constitutes your acceptance of the updated policy.</p>

          <h3>14. Contact Us</h3>

          <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us at <a href="mailto:hello@portexai.com">hello@portexai.com</a>.</p>

          <hr />

          <p>By using our Services, you acknowledge that you have read, understood, and agree to the collection and use of your information as outlined in this Privacy Policy.</p>
        </div>
      </section>
    </main>

    <!-- Site footer -->
    <Footer />
  </div>
</template>

<script>
import Footer from '@/components/Footer.vue'

export default {
  name: 'Privacy',
  components: {
    Footer,
  },
}
</script>

<style>
.markdown-content h1 {
  @apply text-3xl font-bold mb-6;
}
.markdown-content h3 {
  @apply text-2xl font-semibold mt-8 mb-4;
}
.markdown-content h4 {
  @apply text-xl font-semibold mt-6 mb-3;
}
.markdown-content p {
  @apply mb-4;
}
.markdown-content ul {
  @apply list-disc pl-5 mb-4;
}
.markdown-content hr {
  @apply my-6;
}
.markdown-content a {
  @apply text-blue-300 hover:text-blue-200;
}
.markdown-content em {
  @apply italic;
}
.markdown-content strong {
  @apply font-bold;
}
</style>
